/**
 * WorkspaceBrief — top-docked, collapsible plan-node brief inside a build
 * project's workspace view.
 *
 * Owner requirement: when working inside a workspace spawned from a plan
 * node, the user must never lose "why am I here" — the node's purpose,
 * inputs and output, presented EXACTLY as the node is shown in the plan
 * workspace (PlanNodeDetailPanel). The body is the shared
 * <PlanNodeBriefBody/> so the two surfaces cannot drift; this component
 * adds a collapse header (phase chip · title · status pill · chevron).
 *
 * Self-gating: renders null unless the active project is a build project
 * AND a plan node matching the current workspace_id is found. Collapse
 * state persists per workspace for the session (module-level, like the
 * plan drawer's height memory).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useProjectStore, groundingEffectiveCounts } from '../../stores/projectStore';
import { getReportLibraryUrl, authHeaders } from '../../apiService';
import type { DictWorkspaceState } from '../../types/workspaceState';
import type { PlanNodeData, PlanEdgeData } from '../process/PlanGraphView';
import { PlanNodeBriefBody, PhaseChip, ShapeBadge, StatusPill } from '../process/PlanNodeBrief';
import { dictToPlanGraph } from '../../utils/planGraphData';

// Per-workspace collapse memory — survives view switches for the session.
const collapsedByWorkspace = new Map<string, boolean>();
const heightByWorkspace = new Map<string, number>();

const DEFAULT_HEIGHT = 'clamp(180px, 26vh, 280px)';
const MIN_HEIGHT_PX = 160;
const MAX_FRACTION = 0.75;

const clamp = (min: number, value: number, max: number) => Math.min(Math.max(value, min), max);

export default function WorkspaceBrief({
  workspaceId,
}: {
  workspaceId?: string | null;
}) {
  const activeProject = useProjectStore((s) => s.activeProject);
  // The CURRENT workspace's report grounding seal (this workspace's deliverable),
  // read from the store's active dictWorkspace — NOT the plan node. The plan node
  // stays validation_status=approved after the report is edited (the staleness
  // cascade doesn't propagate back to the plan node), so a delivered-but-stale or
  // flagged deliverable would otherwise read a clean "Done".
  const groundingSeal = useProjectStore((s) => s.dictWorkspace?.report_grounding_seal ?? null);
  const dictWorkspaceId = useProjectStore((s) => s.dictWorkspace?.workspace_id ?? null);
  // BAR-505: the CURRENT research workspace's sealed regulatory scope — read
  // from the store's live dictWorkspace (updates on reload_project), same
  // guard as the grounding seal (only when the active workspace IS this one).
  const regulatoryScope = useProjectStore((s) => s.dictWorkspace?.regulatory_scope ?? null);

  const [planGraph, setPlanGraph] = useState<{
    nodes: PlanNodeData[];
    edges: PlanEdgeData[];
  } | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(
    workspaceId ? collapsedByWorkspace.get(workspaceId) ?? false : false,
  );
  const [height, setHeight] = useState<number | null>(
    workspaceId ? heightByWorkspace.get(workspaceId) ?? null : null,
  );
  const bodyRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startY: number; startH: number } | null>(null);

  const projectId = activeProject?.project_id;
  const isBuild = activeProject?.project_type === 'build';
  const planWorkspaceId = projectId ? `ws_${projectId}` : null;

  // Re-read the collapse memory when the user moves between workspaces.
  useEffect(() => {
    if (workspaceId) setCollapsed(collapsedByWorkspace.get(workspaceId) ?? false);
  }, [workspaceId]);

  // Re-read the height memory when the user moves between workspaces.
  useEffect(() => {
    if (workspaceId) setHeight(heightByWorkspace.get(workspaceId) ?? null);
  }, [workspaceId]);

  // Fetch the plan graph (same read as PlanTrainTrack) — the brief lives in
  // a CHILD workspace, so the plan state is never the store's dictWorkspace.
  useEffect(() => {
    if (!isBuild || !planWorkspaceId || !workspaceId) return;
    const userId = activeProject?.user_id || '';
    if (!userId) return;
    let cancelled = false;
    const url = getReportLibraryUrl();
    // BAR-750: auth-gated endpoint — a tokenless fetch 401s under ENFORCE_AUTH and
    // the catch below swallows it, leaving the brief silently empty.
    authHeaders()
      .then((headers) => fetch(`${url}/workspaces/${planWorkspaceId}/state?user_id=${encodeURIComponent(userId)}`, { headers }))
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        if (cancelled || !data?.success || !data.nodes) return;
        setPlanGraph(dictToPlanGraph(data as DictWorkspaceState));
      })
      .catch((err) => console.error('[WorkspaceBrief] plan fetch failed:', err));
    return () => { cancelled = true; };
  }, [isBuild, planWorkspaceId, workspaceId, activeProject?.user_id]);

  const node = useMemo(
    () => planGraph?.nodes.find((n) => n.workspace_id === workspaceId) || null,
    [planGraph, workspaceId],
  );
  const nodeById = useMemo(() => {
    const map = new Map((planGraph?.nodes || []).map((n) => [n.node_id, n]));
    return (id: string) => map.get(id);
  }, [planGraph]);

  // Honest status: a delivered node ("Done") whose deliverable validation is not
  // clean must not read clean. Layer the current workspace's grounding seal over
  // the plan-node lifecycle status — confirmed issues → Blocked, and stale (edited
  // since last check) or unresolved/unverified flags → Needs review. Only applied
  // when the store's active workspace IS this brief's workspace (avoid a transient
  // mismatch while switching), and only downgrades a "done" status — it never
  // upgrades planned/active.
  const effectiveStatus = useMemo<PlanNodeData['status']>(() => {
    const base = (node?.status ?? 'planned') as PlanNodeData['status'];
    if (!node || !groundingSeal || dictWorkspaceId !== workspaceId) return base;
    if (base !== 'done') return base;
    const { confirmed, suspected } = groundingEffectiveCounts(groundingSeal);
    if (confirmed > 0) return 'blocked';
    if (groundingSeal.stale || suspected > 0) return 'needs_review';
    return base;
  }, [node, groundingSeal, dictWorkspaceId, workspaceId]);

  if (!isBuild || !node) return null;

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (workspaceId) collapsedByWorkspace.set(workspaceId, next);
  };

  const getMaxHeight = (body: HTMLDivElement) => {
    const containerH = body.parentElement?.parentElement?.clientHeight ?? window.innerHeight;
    return containerH * MAX_FRACTION;
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const body = bodyRef.current;
    if (!body) return;
    e.preventDefault();
    dragRef.current = { startY: e.clientY, startH: body.getBoundingClientRect().height };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const body = bodyRef.current;
    if (!drag || !body) return;
    const next = Math.round(
      clamp(MIN_HEIGHT_PX, drag.startH + (e.clientY - drag.startY), getMaxHeight(body)),
    );
    setHeight(next);
  };
  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const body = bodyRef.current;
    if (workspaceId && body) {
      const next = Math.round(
        clamp(MIN_HEIGHT_PX, body.getBoundingClientRect().height, getMaxHeight(body)),
      );
      heightByWorkspace.set(workspaceId, next);
      setHeight(next);
    }
    dragRef.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };
  const resetHeight = () => {
    if (workspaceId) heightByWorkspace.delete(workspaceId);
    setHeight(null);
  };

  return (
    <div className="flex-shrink-0 w-full bg-surface border-b border-border-default flex flex-col">
      {/* Header — phase chip · title · status pill · collapse toggle */}
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        title={collapsed ? 'Expand workspace brief' : 'Collapse workspace brief'}
        className="flex items-center gap-2.5 px-4 h-10 flex-shrink-0 min-w-0 w-full text-left hover:bg-bg-warm transition-colors"
      >
        <PhaseChip workspaceType={node.workspace_type} />
        <h3 className="text-[15px] font-semibold text-fg truncate min-w-0">{node.text}</h3>
        <ShapeBadge node={node} />
        <StatusPill status={effectiveStatus} />
        <div className="flex-1" />
        {collapsed
          ? <ChevronDown className="w-4 h-4 text-fg-tertiary flex-shrink-0" />
          : <ChevronUp className="w-4 h-4 text-fg-tertiary flex-shrink-0" />}
      </button>

      {!collapsed && (
        <div
          ref={bodyRef}
          className="relative flex flex-col min-h-0 border-t border-border-default"
          style={{
            height: height != null
              ? `clamp(${MIN_HEIGHT_PX}px, ${height}px, ${MAX_FRACTION * 100}vh)`
              : DEFAULT_HEIGHT,
          }}
        >
          <PlanNodeBriefBody
            node={node}
            edges={planGraph?.edges || []}
            nodeById={nodeById}
            regulatoryScope={dictWorkspaceId === workspaceId ? regulatoryScope : null}
          />
          {/* Resize handle — straddles the body bottom edge; drag to resize, double-click resets */}
          <div
            role="separator"
            aria-orientation="horizontal"
            aria-label="Resize workspace brief"
            title="Drag to resize · double-click to reset"
            className="absolute -bottom-1 left-0 right-0 h-2.5 cursor-ns-resize flex items-center justify-center group z-20"
            style={{ touchAction: 'none' }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onDoubleClick={resetHeight}
          >
            <span className="w-8 h-1 rounded-full bg-border-strong group-hover:bg-fg-tertiary transition-colors" />
          </div>
        </div>
      )}
    </div>
  );
}
